/**
 * ENG-10001: fleet delivery for the Obi Wan review poster.
 *
 * `post-review-findings.mjs` + `obiwan-auth.mjs` are vendored into this package
 * at `apps/cli/assets/review/` and copied into each agent's dir here. That is
 * deliberately the SAME shape as the Stop / isolation / orient / kanban hooks,
 * for the reason recorded at their call site in `manager-worker.ts`: ENG-6233
 * shipped a hook only inside `claudecode-plugin-augmented`, "which the managed
 * fleet never installs", so it silently did nothing on every agent. ENG-6268's
 * fix named manager provisioning as "the actual fleet delivery path". The
 * Smithers plugin copy has exactly that problem — nothing in this monorepo has
 * ever referenced it — so it cannot be the vehicle either.
 *
 * WHY BOTH FILES, SIDE BY SIDE: the poster does `import … from
 * './obiwan-auth.mjs'`, a relative specifier. They must land in one directory
 * or the import fails at runtime. Both are dependency-free (node builtins only),
 * which is what makes flat-file vendoring viable at all.
 *
 * WHY UNCONDITIONAL, rather than gated on an agent being the reviewer:
 * provisioning this grants NO capability the agent does not already hold. The
 * `agt-runtime` isolation image bakes in the `gh` CLI (ENG-7662,
 * `host-infra/agt-runtime/Dockerfile`) and ENG-7506 materialises a GitHub token
 * into the session env as both GITHUB_ACCESS_TOKEN and GITHUB_TOKEN — so any
 * agent that could be induced to post a PR comment can already do so with
 * `gh api`. This file is a safer wrapper over an authority already present, not
 * a new grant. (It is also strictly better than the alternative: an agent
 * lacking the poster improvises raw `gh api` and loses the one property worth
 * most here — the structural impossibility of a blocking REQUEST_CHANGES
 * verdict.) Gating on the reviewer role is a live option once the /host/agents
 * payload carries roles; it does not today.
 *
 * NOTHING CALLS THE POSTER YET. This ships the delivery path only. The trigger
 * and the `FLAG_REGISTRY` posting gate are the remaining ENG-10001 items and are
 * deliberately not wired here.
 *
 * THE REVIEW SKILLS NO LONGER TRAVEL THIS LANE (ENG-10374). `pr-review` and
 * `pr-review-sweep` used to be vendored beside the poster and installed through
 * the adapter's `installSkillFiles`. They were provisioned to every managed
 * Claude Code agent, customers' included, against the ENG-10006 shape in which
 * an agent on the fleet ran the sweep. ENG-10296 replaced that with a dedicated
 * runner whose orchestration is code, and `packages/obiwan-runner` holds its own
 * calibrated `REVIEW_BRIEF` (`brief.ts`) rather than reading a skill body — so
 * the skills reached the whole fleet and were invoked by nothing, which is
 * ENG-10001's own "exposure with no purpose".
 *
 * This removed the DELIVERY PATH, not the installed base. `installSkillFiles`
 * only ever writes, and the sole skill-directory prune in the adapter is
 * name-specific (`knowledge-*`, `core-knowledge`), so every agent provisioned
 * before that change keeps both skills indefinitely. Retracting them is a
 * separate change with a different risk profile — a fleet-wide delete delivered
 * by CLI auto-publish — and has not been made.
 *
 * The fail-closed identity check IS now in place (`refuseIdentity` in
 * `post-review-findings.mjs`, `EXPECTED_APP_SLUG` in `obiwan-auth.mjs`), which
 * is what makes provisioning-before-a-caller safe rather than merely early: the
 * asset that lands on a host cannot write to a PR under the platform's token,
 * whoever induces it to run.
 */

import { copyFileSync, chmodSync, existsSync, mkdirSync, readFileSync } from 'node:fs';
import { dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';
import { getFramework } from '@augmented/core';
// Side-effect import: the claude-code adapter self-registers at the foot of
// this module (`registerFramework(claudeCodeAdapter)`), and `getAgentDir` is
// module-private, so the adapter's method is the only public route to the
// ADR-0049 id-keyed agent dir. `manager-worker.ts` happens to load the adapter
// already, which is exactly why depending on that implicitly would work in the
// manager and fail anywhere else — including under test, where it did.
import '@augmented/core/provisioning/frameworks/claudecode/index.js';

/** The entrypoint an agent invokes. Named, not indexed out of ASSET_FILES. */
const POSTER_FILENAME = 'post-review-findings.mjs';
/** Imported by the entrypoint via a RELATIVE specifier — must land beside it. */
const AUTH_FILENAME = 'obiwan-auth.mjs';

/** Mode 0o755 for the executable entrypoint, 0o644 for the imported module. */
const ASSET_FILES: ReadonlyArray<readonly [name: string, mode: number]> = [
  [POSTER_FILENAME, 0o755],
  [AUTH_FILENAME, 0o644],
];

/** Subdirectory under the agent's dir that holds the vendored review assets. */
export const REVIEW_ASSET_DIRNAME = 'review';

/**
 * Walk up from this module to the bundled `assets/review/` directory. Covers
 * the same three layouts as `resolveBundledAssetPath` in
 * `impersonate-statusline.ts`, and for the same reason — the module sits at a
 * different depth in dev (tsx, src/lib/) than when bundled (tsup, dist/).
 */
export function resolveBundledReviewAssetDir(): string {
  const moduleDir = dirname(fileURLToPath(import.meta.url));
  const candidates = [
    // Built output: dist/<chunk>.js → dist/assets/review
    join(moduleDir, 'assets', REVIEW_ASSET_DIRNAME),
    // Built output sibling case: dist/<sub>/<chunk>.js → dist/assets/review
    join(moduleDir, '..', 'assets', REVIEW_ASSET_DIRNAME),
    // Dev source: src/lib/review-poster-asset.ts → ../../assets/review
    join(moduleDir, '..', '..', 'assets', REVIEW_ASSET_DIRNAME),
  ];
  for (const candidate of candidates) {
    // Probe a FILE, not the directory: an empty `assets/review/` (a partial
    // build, or a stale dist) would satisfy a directory check and then fail
    // later at import time, which is the harder failure to read.
    if (existsSync(join(candidate, POSTER_FILENAME))) return candidate;
  }
  throw new Error(
    `[review-poster] could not locate bundled assets; tried:\n  ${candidates.join('\n  ')}`,
  );
}

/** Where the review assets live for an agent. */
export function reviewPosterDir(codeName: string): string {
  return join(getFramework('claude-code').getAgentDir(codeName), REVIEW_ASSET_DIRNAME);
}

/** Absolute path to the poster entrypoint an agent should invoke. */
export function reviewPosterPath(codeName: string): string {
  return join(reviewPosterDir(codeName), POSTER_FILENAME);
}

/**
 * Copy the vendored review assets into `<agentDir>/review/` (idempotent).
 *
 * Byte-compares before writing so a re-provision on every session start does
 * not churn mtimes — but always re-asserts the mode, since a copy that landed
 * without the exec bit is a failure that only shows up at invocation.
 */
export function provisionReviewPoster(
  codeName: string,
  sourceDir: string = resolveBundledReviewAssetDir(),
): void {
  const destDir = reviewPosterDir(codeName);
  mkdirSync(destDir, { recursive: true });

  for (const [name, mode] of ASSET_FILES) {
    const src = join(sourceDir, name);
    const dest = join(destDir, name);
    if (!existsSync(src)) {
      throw new Error(`[review-poster] bundled asset missing: ${src}`);
    }
    if (!existsSync(dest) || !readFileSync(src).equals(readFileSync(dest))) {
      copyFileSync(src, dest);
    }
    chmodSync(dest, mode);
  }
}
